import { Pencil } from "lucide-react";
import { useState } from "react";
import { DataTable } from "@/components/shared/data-table";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useEmailTemplate, useEmailTemplates } from "@/hooks/use-email";
import { EditTemplateDialog } from "./EditTemplateDialog";

export function TemplatesCard() {
  const templates = useEmailTemplates();
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const detail = useEmailTemplate(editingKey);

  return (
    <Card className="border-border/50">
      <CardHeader>
        <CardTitle className="text-lg">Templates</CardTitle>
        <CardDescription>
          Edit the copy of the React Email templates registered in{" "}
          <code>src/lib/email/templates</code>. Layout, escaping, and CTA URLs stay in code; only
          subject, preheader, heading, body paragraphs, and CTA labels are stored in the database.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {templates.isError ? (
          <ErrorState
            message={templates.error?.message ?? "Failed to load templates"}
            onRetry={() => templates.refetch()}
          />
        ) : (
          <DataTable
            headers={[
              { label: "Template" },
              { label: "Key" },
              { label: "Status" },
              { label: "Updated", className: "hidden md:table-cell" },
              { label: "", className: "text-right" },
            ]}
            loading={templates.isLoading}
          >
            {templates.data?.map((t) => (
              <tr
                key={t.key}
                className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
              >
                <td className="px-4 py-3 font-medium">{t.label}</td>
                <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{t.key}</td>
                <td className="px-4 py-3">
                  {t.editable ? (
                    <Badge
                      variant="outline"
                      className="border-transparent bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                    >
                      Editable
                    </Badge>
                  ) : (
                    <Badge
                      variant="outline"
                      className="border-transparent bg-muted text-muted-foreground"
                    >
                      Code-only
                    </Badge>
                  )}
                </td>
                <td className="hidden md:table-cell px-4 py-3 text-muted-foreground">
                  {t.updatedAt ? new Date(t.updatedAt).toLocaleString() : "—"}
                </td>
                <td className="px-4 py-3 text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditingKey(t.key)}
                    disabled={!t.editable}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    {t.editable ? "Edit" : "View"}
                  </Button>
                </td>
              </tr>
            ))}
          </DataTable>
        )}
      </CardContent>

      {editingKey && detail.data && (
        <EditTemplateDialog
          key={editingKey}
          open={editingKey !== null}
          onOpenChange={(open) => !open && setEditingKey(null)}
          template={detail.data}
        />
      )}
    </Card>
  );
}
